import React, { useState, useEffect, useMemo, useRef } from "react";
import { Helmet } from "react-helmet";
import { Search, ChevronLeft, ChevronRight, Filter } from "lucide-react";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import SectionHeader from "../components/common/sectionHeader";
import Article from "../components/articles/article";

import INFO from "../data/user";
import SEO from "../data/seo";
import myArticles from "../data/articles";

import "./styles/articles.css";

const ITEMS_PER_PAGE = 6;

const CATEGORIES = [
	"All",
	"PostgreSQL & Databases",
	"Architecture & System Design",
	"Java & Spring",
	"Security & APIs",
	"DevOps & Reliability",
	"Applied AI",
	"Frontend",
];

const Articles = () => {
	const [currentPage, setCurrentPage] = useState(1);
	const [selectedCategory, setSelectedCategory] = useState("All");
	const [searchQuery, setSearchQuery] = useState("");
	const articlesHeaderRef = useRef(null);

	const currentSEO = SEO.find((item) => item.page === "articles") || SEO[2];

	// Map articles with permanent ID & sort chronologically (newest first)
	const allArticles = useMemo(() => {
		return [...myArticles]
			.map((getArticle, index) => ({
				...getArticle(),
				id: index + 1,
			}))
			.sort((a, b) => new Date(b.date) - new Date(a.date));
	}, []);

	// Filter articles by category and search query
	const filteredArticles = useMemo(() => {
		return allArticles.filter((article) => {
			const matchesCategory =
				selectedCategory === "All" ||
				(article.category && article.category.toLowerCase().includes(selectedCategory.toLowerCase())) ||
				(article.keywords && article.keywords.some((kw) => kw.toLowerCase().includes(selectedCategory.toLowerCase())));

			const query = searchQuery.toLowerCase().trim();
			const matchesSearch =
				!query ||
				article.title.toLowerCase().includes(query) ||
				article.description.toLowerCase().includes(query) ||
				(article.keywords && article.keywords.some((kw) => kw.toLowerCase().includes(query)));

			return matchesCategory && matchesSearch;
		});
	}, [allArticles, selectedCategory, searchQuery]);

	// Reset page to 1 whenever filter or search query changes
	useEffect(() => {
		setCurrentPage(1);
	}, [selectedCategory, searchQuery]);

	// Calculate pagination slice
	const totalPages = Math.ceil(filteredArticles.length / ITEMS_PER_PAGE) || 1;
	const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
	const currentArticles = filteredArticles.slice(startIndex, startIndex + ITEMS_PER_PAGE);

	const handlePageChange = (pageNumber) => {
		if (pageNumber >= 1 && pageNumber <= totalPages) {
			setCurrentPage(pageNumber);
			if (articlesHeaderRef.current) {
				articlesHeaderRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
			}
		}
	};

	// Generate smart pagination page numbers (e.g. 1, 2, 3 ... 9)
	const getPageNumbers = () => {
		const pages = [];
		const maxVisible = 5;

		if (totalPages <= maxVisible) {
			for (let i = 1; i <= totalPages; i++) pages.push(i);
		} else {
			pages.push(1);
			if (currentPage > 3) pages.push("...");

			const start = Math.max(2, currentPage - 1);
			const end = Math.min(totalPages - 1, currentPage + 1);

			for (let i = start; i <= end; i++) {
				if (!pages.includes(i)) pages.push(i);
			}

			if (currentPage < totalPages - 2) pages.push("...");
			pages.push(totalPages);
		}
		return pages;
	};

	return (
		<React.Fragment>
			<Helmet>
				<title>{`Articles | ${INFO.main.name}`}</title>
				<meta name="description" content={currentSEO.description} />
				<meta name="keywords" content={currentSEO.keywords.join(", ")} />
			</Helmet>

			<div className="page-content">
				<NavBar active="articles" />

				<main className="content-wrapper">
					<div className="articles-page-container" ref={articlesHeaderRef}>
						<SectionHeader
							tag="Technical Writing"
							title="Articles & Engineering"
							highlight="Insights"
							subtitle="Publication-grade analysis on PostgreSQL, Java, Spring Boot, distributed systems, system design, and AI architecture."
						/>

						{/* Search & Category Filter Toolbar */}
						<div className="articles-toolbar">
							<div className="articles-search-wrapper">
								<Search className="articles-search-icon" size={18} />
								<input
									type="text"
									className="articles-search-input"
									placeholder="Search articles by title, topic, or keyword..."
									value={searchQuery}
									onChange={(e) => setSearchQuery(e.target.value)}
								/>
								{searchQuery && (
									<button className="articles-clear-search" onClick={() => setSearchQuery("")}>
										Clear
									</button>
								)}
							</div>

							<div className="articles-category-bar">
								<div className="category-label">
									<Filter size={14} />
									<span>Filter:</span>
								</div>
								<div className="category-pills">
									{CATEGORIES.map((category) => (
										<button
											key={category}
											className={`category-pill ${selectedCategory === category ? "active" : ""}`}
											onClick={() => setSelectedCategory(category)}
										>
											{category}
										</button>
									))}
								</div>
							</div>
						</div>

						{/* Pagination Info Badge */}
						<div className="articles-meta-info">
							<span>
								Showing <strong>{filteredArticles.length === 0 ? 0 : startIndex + 1}</strong>–
								<strong>{Math.min(startIndex + ITEMS_PER_PAGE, filteredArticles.length)}</strong> of{" "}
								<strong>{filteredArticles.length}</strong> articles
							</span>
							{selectedCategory !== "All" && <span className="filter-badge">Category: {selectedCategory}</span>}
						</div>

						{/* Articles Grid */}
						{currentArticles.length > 0 ? (
							<div className="articles-grid">
								{currentArticles.map((article) => (
									<Article
										key={article.id}
										date={article.date}
										title={article.title}
										description={article.description}
										link={`/article/${article.id}`}
									/>
								))}
							</div>
						) : (
							<div className="articles-empty-state">
								<h3>No matching articles found</h3>
								<p>Try clearing your search query or choosing another category filter.</p>
								<button
									className="btn-primary"
									onClick={() => {
										setSearchQuery("");
										setSelectedCategory("All");
									}}
								>
									Reset Filters
								</button>
							</div>
						)}

						{/* Pagination Controls */}
						{totalPages > 1 && (
							<div className="pagination-wrapper">
								<button
									className="pagination-nav-btn"
									onClick={() => handlePageChange(currentPage - 1)}
									disabled={currentPage === 1}
								>
									<ChevronLeft size={16} />
									<span>Previous</span>
								</button>

								<div className="pagination-numbers">
									{getPageNumbers().map((page, idx) =>
										page === "..." ? (
											<span key={`ellipsis-${idx}`} className="pagination-ellipsis">
												...
											</span>
										) : (
											<button
												key={page}
												className={`pagination-num-btn ${currentPage === page ? "active" : ""}`}
												onClick={() => handlePageChange(page)}
											>
												{page}
											</button>
										)
									)}
								</div>

								<button
									className="pagination-nav-btn"
									onClick={() => handlePageChange(currentPage + 1)}
									disabled={currentPage === totalPages}
								>
									<span>Next</span>
									<ChevronRight size={16} />
								</button>
							</div>
						)}
					</div>

					<div className="page-footer">
						<Footer />
					</div>
				</main>
			</div>
		</React.Fragment>
	);
};

export default Articles;
